import { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Card,
  Typography,
  Grid,
  Paper,
  Avatar,
  LinearProgress,
  Alert,
  Snackbar,
  Tooltip,
  Fab,
  CircularProgress,
} from "@mui/material";
import {
  DataGrid,
  gridClasses,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AdminApiModule from "../../components/AdminApi/Api/api";
import AddClient from "../Admin/AddClient";
import ClientDetailsDialog from "./AdminViewClient";
import {
  canViewClients,
  canAddClients,
  canUpdateClients,
  canDeleteClients,
  canViewPincodeClient,
  hasFullClientAccess,
} from "../../utility/permissionUtils";

function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Pagination
      color="primary"
      count={pageCount}
      page={page + 1}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
      className="mt-4 flex justify-center"
    />
  );
}

const Clients = () => {
  const [clientsList, setClientsList] = useState([]);
  const [isAddClientFormVisible, setIsAddClientFormVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const reduxState = useSelector((state) => state.userReducer);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  // Get user permissions
  const userPermissions = reduxState?.adminDetails?.role_id?.permissions;

  // Check permissions
  const hasViewAccess =
    canViewClients(userPermissions) || hasFullClientAccess(userPermissions);
  const hasAddAccess =
    canAddClients(userPermissions) || hasFullClientAccess(userPermissions);
  const hasUpdateAccess =
    canUpdateClients(userPermissions) || hasFullClientAccess(userPermissions);
  const hasDeleteAccess =
    canDeleteClients(userPermissions) || hasFullClientAccess(userPermissions);
  const hasPincodeAccess =
    canViewPincodeClient(userPermissions) ||
    hasFullClientAccess(userPermissions);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const columns = [
    {
      field: "clientId",
      headerName: "ID",
      flex: 0.7,
      minWidth: 80,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          className="bg-blue-100 text-blue-800 font-medium"
        />
      ),
    },
    {
      field: "clientName",
      headerName: "Client Name",
      flex: 1.5,
      minWidth: 160,
      renderCell: (params) => (
        <div className="flex items-center">
          <Avatar className="w-8 h-8 mr-2 bg-indigo-500">
            {params.value?.charAt(0) || "C"}
          </Avatar>
          <Typography variant="body2" className="font-medium truncate">
            {params.value}
          </Typography>
        </div>
      ),
    },
    {
      field: "shortCode",
      headerName: "Shortcode",
      flex: 1,
      minWidth: 100,
      hide: isMobile,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          className="bg-indigo-100 text-indigo-800 truncate"
        />
      ),
    },
    {
      field: "productName",
      headerName: "Product",
      flex: 1.2,
      minWidth: 140,
      hide: isMobile,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          className="bg-green-100 text-green-800 truncate"
        />
      ),
    },
    {
      field: "officialEmail",
      headerName: "Email",
      flex: 1.8,
      minWidth: 180,
      hide: isMobile || isTablet,
      renderCell: (params) => (
        <div className="flex items-center">
          <EmailIcon className="text-gray-500 mr-1" fontSize="small" />
          <Typography variant="body2" className="truncate">
            {params.value}
          </Typography>
        </div>
      ),
    },
    {
      field: "pocName",
      headerName: "POC",
      flex: 1.2,
      minWidth: 130,
      hide: isMobile,
    },
    {
      field: "city",
      headerName: "Location",
      flex: 1,
      minWidth: 120,
      hide: isMobile || isTablet,
      renderCell: (params) => (
        <div className="flex items-center">
          <LocationIcon className="text-gray-500 mr-1" fontSize="small" />
          <Typography variant="body2" className="truncate">
            {params.value}
          </Typography>
        </div>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="flex space-x-1">
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={() => handleViewClient(params.row)}
              className="text-blue-500 hover:bg-blue-50"
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Edit Button - Only show if user has update access */}
          {hasUpdateAccess &&
            (editLoading && selectedClient?.id === params.row.id ? (
              <CircularProgress size={20} className="text-green-500" />
            ) : (
              <Tooltip title="Edit client">
                <IconButton
                  size="small"
                  onClick={() => handleEditClient(params.row)}
                  className="text-green-500 hover:bg-green-50"
                  disabled={editLoading}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ))}

          {/* Delete Button - Only show if user has delete access */}
          {hasDeleteAccess && (
            <Tooltip title="Delete client">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(params.row)}
                className="text-red-500 hover:bg-red-50"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const handleEditClient = async (client) => {
    if (!hasUpdateAccess) {
      showSnackbar("You don't have permission to edit clients", "error");
      return;
    }

    try {
      setEditLoading(true);
      const response = await new AdminApiModule().getClientById(client.id);

      if (response.data) {
        setEditingClient(response.data);
        setIsEditMode(true);
        setIsAddClientFormVisible(true);
      } else {
        showSnackbar("Failed to fetch client details", "error");
      }
    } catch (err) {
      console.error("Error fetching client details:", err);
      showSnackbar("Failed to fetch client details", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // FIXED: Properly handle nested objects and ensure all values are strings or numbers
  const getParsedData = (data) => {
    return data.map((item) => ({
      id: item._id,
      clientId: item?.client_id || "-",
      clientName: item?.client_name || "-",
      productName: item?.product_name || "-",
      productCategory: item?.product_category || "-",
      specificRequirement: item?.specific_requirement || "-",
      price: item?.price || 0,
      officialEmail: item?.official_email || "-",
      pocName: item?.client_poc?.person_name || "-",
      pocDesignation: item?.client_poc?.person_designation || "-",
      pocPhone: item?.client_poc?.phone?.mobile_number || "-",
      addressLine: item?.address_details?.address_line || "-",
      city: item?.address_details?.city || "-",
      state: item?.address_details?.state || "-",
      pincode: hasPincodeAccess ? item?.address_details?.pincode || "-" : "***", // Hide pincode if no permission
      country: item?.address_details?.country || "-",
      gstNumber: item?.gst_number || "-",
      createdAt: item?.createdAt || "-",
      clientQrId: item?.qr_details?.qr_id || "-",
      shortCode: item?.short_code || "-",
      // FIX: Ensure serviceAreas is properly handled and doesn't contain React-incompatible objects
      serviceAreas: item?.serviceAreas
        ? {
            categories: Array.isArray(item.serviceAreas.categories)
              ? item.serviceAreas.categories.map((cat) => ({
                  category_id: cat.category_id || "",
                  payment_way: cat.payment_way
                    ? {
                        isPayViaClient: Boolean(cat.payment_way.isPayViaClient),
                        isPayViaCustomer: Boolean(
                          cat.payment_way.isPayViaCustomer
                        ),
                        ticketPicPrize:
                          Number(cat.payment_way.ticketPicPrize) || 0,
                      }
                    : {
                        isPayViaClient: false,
                        isPayViaCustomer: true,
                        ticketPicPrize: 0,
                      },
                }))
              : [],
            states: Array.isArray(item.serviceAreas.states)
              ? item.serviceAreas.states
              : [],
            cities: Array.isArray(item.serviceAreas.cities)
              ? item.serviceAreas.cities
              : [],
            pincodes: Array.isArray(item.serviceAreas.pincodes)
              ? item.serviceAreas.pincodes
              : [],
            primary_services: Array.isArray(item.serviceAreas.primary_services)
              ? item.serviceAreas.primary_services
              : [],
          }
        : {
            categories: [],
            states: [],
            cities: [],
            pincodes: [],
            primary_services: [],
          },
      aadhaarNumber: item?.aadhaar_number || "-",
      panNumber: item?.pan_number || "-",
      tinNumber: item?.tin_number || "-",
      // FIX: Ensure bankDetails is properly handled
      bankDetails: item?.bank_details
        ? {
            bank_name: item.bank_details.bank_name || "",
            bank_account_number: item.bank_details.bank_account_number || "",
            branch: item.bank_details.branch || "",
            ifsc_code: item.bank_details.ifsc_code || "",
          }
        : {
            bank_name: "",
            bank_account_number: "",
            branch: "",
            ifsc_code: "",
          },
    }));
  };

  const getAllClients = () => {
    if (!hasViewAccess) {
      showSnackbar("You don't have permission to view clients", "error");
      return;
    }

    setLoading(true);
    new AdminApiModule()
      .getAllClients()
      .then((res) => {
        let parsedData = [];
        if (res && res.data && res.data.length > 0) {
          parsedData = getParsedData(res.data);
        }
        setClientsList(parsedData);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        showSnackbar("Failed to load clients", "error");
      });
  };

  const handleDeleteClick = (client) => {
    if (!hasDeleteAccess) {
      showSnackbar("You don't have permission to delete clients", "error");
      return;
    }
    setSelectedClient(client);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedClient) {
      const payload = { clientId: selectedClient.id };
      new AdminApiModule()
        .deleteClient(payload)
        .then((res) => {
          getAllClients();
          setDeleteDialogOpen(false);
          setSelectedClient(null);
          showSnackbar("Client deleted successfully");
        })
        .catch((err) => {
          console.log(err);
          setDeleteDialogOpen(false);
          setSelectedClient(null);
          showSnackbar("Failed to delete client", "error");
        });
    }
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setViewDialogOpen(true);
  };

  const toggleAddClientForm = () => {
    if (!hasAddAccess && !isEditMode) {
      showSnackbar("You don't have permission to add clients", "error");
      return;
    }

    if (isAddClientFormVisible && isEditMode) {
      setIsEditMode(false);
      setEditingClient(null);
    }
    setIsAddClientFormVisible(!isAddClientFormVisible);
  };

  useEffect(() => {
    if (hasViewAccess) {
      getAllClients();
    }
  }, [hasViewAccess]);

  const handleClientUpdated = () => {
    getAllClients();
    setIsEditMode(false);
    setEditingClient(null);
    showSnackbar("Client updated successfully");
  };

  // Check if user has any client access at all
  if (!hasViewAccess) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 p-4">
        <Card className="p-6 max-w-md w-full shadow-lg rounded-xl text-center">
          <BusinessIcon className="text-red-500 text-5xl mb-4 mx-auto" />
          <Typography variant="h5" className="font-bold text-gray-800 mb-2">
            Access Denied
          </Typography>
          <Typography variant="body1" className="text-gray-600 mb-4">
            You don't have permission to view clients. Please contact your
            administrator.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(-1)}
            className="bg-blue-500 hover:bg-blue-600"
          >
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-6 bg-white rounded-2xl shadow-sm p-4 md:p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <Typography variant="h4" className="font-bold text-gray-800">
                Client Management
              </Typography>
              <Typography variant="body1" className="text-gray-600 mt-1">
                Manage your clients and their information
              </Typography>
            </div>

            <div className="flex flex-col xs:flex-row gap-2 w-full md:w-auto">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={getAllClients}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 whitespace-nowrap"
              >
                Refresh
              </Button>

              {hasAddAccess && (
                <Button
                  variant="contained"
                  onClick={toggleAddClientForm}
                  startIcon={<AddIcon />}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-md whitespace-nowrap"
                >
                  {isAddClientFormVisible ? "Hide Form" : "Add Client"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-blue-500">
            <Typography variant="body2" className="text-gray-500">
              Total Clients
            </Typography>
            <Typography variant="h4" className="font-bold text-gray-800">
              {clientsList.length}
            </Typography>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-green-500">
            <Typography variant="body2" className="text-gray-500">
              Active
            </Typography>
            <Typography variant="h4" className="font-bold text-gray-800">
              {clientsList.length}
            </Typography>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-purple-500">
            <Typography variant="body2" className="text-gray-500">
              This Month
            </Typography>
            <Typography variant="h4" className="font-bold text-gray-800">
              0
            </Typography>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-red-500">
            <Typography variant="body2" className="text-gray-500">
              Inactive
            </Typography>
            <Typography variant="h4" className="font-bold text-gray-800">
              0
            </Typography>
          </div>
        </div>

        {/* Data Grid */}
        <Paper className="rounded-2xl shadow-sm overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-100">
            <Typography variant="h6" className="font-semibold text-gray-800">
              Client List
            </Typography>
          </div>

          <div style={{ height: 500, width: "100%" }}>
            <DataGrid
              rows={clientsList}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[5, 10, 20, 50]}
              pagination
              loading={loading}
              components={{
                Pagination: CustomPagination,
                LoadingOverlay: LinearProgress,
              }}
              sx={{
                border: 0,
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#f9fafb",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  padding: "0 16px",
                  borderBottom: "2px solid #e5e7eb",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  },
                  "&:nth-of-type(even)": {
                    backgroundColor: "#fafafa",
                  },
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: "white",
                },
                "& .MuiDataGrid-footerContainer": {
                  backgroundColor: "#f9fafb",
                  borderTop: "1px solid #e5e7eb",
                  padding: "16px",
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
                "& .MuiDataGrid-cellContent": {
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-loadingOverlay": {
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                },
              }}
              getRowHeight={() => "auto"}
              disableSelectionOnClick
            />
          </div>
        </Paper>

        {/* Add Client Form */}
        {isAddClientFormVisible && hasAddAccess && (
          <div className="mb-6 bg-white rounded-2xl shadow-sm overflow-hidden">
            <AddClient
              isFormVisible={isAddClientFormVisible}
              onToggleForm={toggleAddClientForm}
              onClientAdded={getAllClients}
              editingClient={isEditMode ? editingClient : null}
              isEditMode={isEditMode}
              onCancelEdit={() => {
                setIsEditMode(false);
                setEditingClient(null);
              }}
              hasPincodeAccess={hasPincodeAccess}
            />
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{ className: "rounded-2xl max-w-md w-full" }}
        >
          <DialogTitle className="font-bold text-gray-800 bg-red-50">
            Confirm Delete
          </DialogTitle>
          <DialogContent className="pt-4">
            <div className="flex items-center mb-4">
              <div className="bg-red-100 p-2 rounded-full mr-3">
                <DeleteIcon className="text-red-500" />
              </div>
              <Typography variant="body1">
                Are you sure you want to delete client{" "}
                <span className="font-semibold">
                  "{selectedClient?.clientName}"
                </span>
                ? This action cannot be undone.
              </Typography>
            </div>
          </DialogContent>
          <DialogActions className="p-4 border-t border-gray-100">
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              className="text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              className="bg-red-500 text-white hover:bg-red-600 ml-2 px-4"
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Client Details Dialog */}
        <ClientDetailsDialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          client={selectedClient}
          hasPincodeAccess={hasPincodeAccess}
          hasUpdateAccess={hasUpdateAccess}
          hasDeleteAccess={hasDeleteAccess}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            className="rounded-xl shadow-lg"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Floating Action Button for Mobile */}
        {hasAddAccess && isMobile && (
          <Fab
            color="primary"
            aria-label="add client"
            onClick={toggleAddClientForm}
            className="fixed bottom-6 right-6 bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg"
          >
            {isAddClientFormVisible ? <DeleteIcon /> : <AddIcon />}
          </Fab>
        )}
      </div>
    </div>
  );
};

export default Clients;
